'use client';

import {
  QueryClient,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import { PaymentMethodMutationResponse } from '@sola/schema/dotcom/proxy-api/paymentMethods.js';
import type {
  GraphQLCommitment,
  GraphQLSupporterPaymentMethod,
} from '@sola/schema/dotcom/supporters/schema.generated.js';
import { UserError } from '@sola/shared-utils/errors-user.js';
import { MUTATION_KEYS, QUERY_KEYS } from '@sola/supporter-sdk/constants.js';
import { useReadyForQueries } from '@sola/supporter-sdk/hooks/base.js';

import {
  CommitmentPaymentPlanUpdateRes,
  updateCommitmentPaymentPlan,
} from './requests';

/**
 * Update the commitment cache with the payment plan results.
 */
export const updateCommitmentCache = (
  queryClient: QueryClient,
  commitmentUpdates?: PaymentMethodMutationResponse['commitmentUpdates'],
) => {
  if (!commitmentUpdates?.updated.length) {
    return;
  }

  queryClient.setQueryData(
    [QUERY_KEYS.supporterCommitments],
    (currentCommitments: GraphQLCommitment[] = []) => {
      // Create lookup so we don't have to run the loop every time.
      const updatedById: Record<string, GraphQLCommitment> = {};
      commitmentUpdates.updated.forEach((commitment) => {
        if (!commitment?.internalId) {
          return;
        }
        updatedById[commitment.internalId] = commitment;
      });

      return currentCommitments.map(
        (commitment) => updatedById[commitment.internalId] || commitment,
      );
    },
  );
};

/**
 * Hook to update a commitment payment plan.
 */
export const useCommitmentPaymentPlanUpdate = (commitmentId?: string) => {
  const queryClient = useQueryClient();
  const { globalId } = useReadyForQueries();

  return useMutation<
    CommitmentPaymentPlanUpdateRes,
    UserError,
    {
      newPaymentMethodId: GraphQLSupporterPaymentMethod['id'];
      paymentDay?: number;
    }
  >({
    mutationKey: [MUTATION_KEYS.commitmentPaymentPlanUpdate, commitmentId],
    mutationFn: ({ newPaymentMethodId, paymentDay }) => {
      return updateCommitmentPaymentPlan(
        globalId,
        commitmentId,
        newPaymentMethodId,
        paymentDay,
      );
    },
    onSuccess: (res, update) => {
      if (!res?.success) {
        return;
      }

      queryClient.setQueryData(
        [QUERY_KEYS.supporterCommitments],
        (commitments?: GraphQLCommitment[]) =>
          commitments?.map((commitment) =>
            commitment.internalId === commitmentId
              ? {
                  ...commitment,
                  paymentMethodId: update.newPaymentMethodId,
                  paymentPullDay: update.paymentDay,
                }
              : commitment,
          ),
      );
    },
  });
};
